import { useCallback, useEffect, useState } from 'react';

export interface UseInfiniteScrollSentinelOptions {
  hasMore: boolean;
  loading: boolean;
  onLoadMore: () => void;
  rootMargin?: string;
}

function findScrollParent(element: HTMLElement): HTMLElement | null {
  let parent = element.parentElement;
  while (parent) {
    const { overflowY } = window.getComputedStyle(parent);
    if (overflowY === 'auto' || overflowY === 'scroll') {
      return parent;
    }
    parent = parent.parentElement;
  }
  return null;
}

/**
 * Returns a ref for an element placed after the list; `onLoadMore` runs when
 * it comes within `rootMargin` of the nearest scroll container.
 */
export function useInfiniteScrollSentinel({
  hasMore,
  loading,
  onLoadMore,
  rootMargin = '800px',
}: UseInfiniteScrollSentinelOptions) {
  const [sentinel, setSentinel] = useState<HTMLElement | null>(null);

  const sentinelRef = useCallback((node: HTMLElement | null) => {
    setSentinel(node);
  }, []);

  useEffect(() => {
    if (!sentinel || !hasMore || loading) {
      return;
    }

    // Recreated after every load, so a sentinel that is still in range
    // (short pages, tall screens) triggers the next load immediately.
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          onLoadMore();
        }
      },
      { root: findScrollParent(sentinel), rootMargin }
    );
    observer.observe(sentinel);

    return () => observer.disconnect();
  }, [sentinel, hasMore, loading, onLoadMore, rootMargin]);

  return sentinelRef;
}
